import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';

import { PreApplicationService } from '../../../../core/applications/pre-application.service';
import {
  PRE_RECOMMENDATION_LABELS,
  PRE_REVIEW_STATUS_META,
  PreApplicationOpsRow,
  PreApplicationRecommendation,
  PreApplicationReviewStatus,
} from '../../../../core/applications/pre-application.types';
import { StatusChipComponent } from '../../../../shared/status-chip/status-chip';
import { IconComponent } from '../../../../shared/icon/icon';
import { DashPipe } from '../../../../shared/pipes/dash.pipe';

/**
 * The pre-application review worklist (Screens 9.7 / 9.2 pattern).
 *
 * Ops discovers work here: there is no per-submission email and no SLA (ruling
 * Q4). Defaults to the SUBMITTED queue — the records actually awaiting a decision.
 */
@Component({
  selector: 'app-pre-application-queue',
  imports: [
    MatCardModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatTooltipModule,
    RouterLink,
    DatePipe,
    StatusChipComponent,
    IconComponent,
    DashPipe,
  ],
  templateUrl: './pre-application-queue.html',
  styleUrl: './pre-application-queue.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreApplicationQueueComponent implements OnInit {
  private readonly preApplications = inject(PreApplicationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly rows = signal<PreApplicationOpsRow[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);

  /** The queue opens on the records awaiting a decision. */
  protected readonly statusFilter = signal<PreApplicationReviewStatus | 'ALL'>('SUBMITTED');
  protected readonly ordering = signal<'submitted_at' | '-submitted_at'>('-submitted_at');

  protected readonly pageSize = signal(25);
  protected readonly pageIndex = signal(0);

  /** The filters a reviewer can switch between, in lifecycle order. *More
   *  information required* is not offered (FLOW-401, PO 2026-10-27 §3): nothing
   *  can put a record in that state since FLOW-381; older records still read it
   *  under *All*. */
  protected readonly filters: { value: PreApplicationReviewStatus | 'ALL'; label: string }[] = [
    { value: 'SUBMITTED', label: PRE_REVIEW_STATUS_META.SUBMITTED.label },
    { value: 'PROCEEDED', label: PRE_REVIEW_STATUS_META.PROCEEDED.label },
    { value: 'DECLINED', label: PRE_REVIEW_STATUS_META.DECLINED.label },
    { value: 'ALL', label: 'All' },
  ];

  protected readonly columns = [
    'submitted',
    'entity',
    'submitted_by',
    'recommendation',
    'documents',
    'status',
    'assignee',
  ];

  protected readonly skeletonRows = [1, 2, 3, 4, 5];

  protected readonly pagedRows = computed(() => {
    const start = this.pageIndex() * this.pageSize();
    return this.rows().slice(start, start + this.pageSize());
  });

  protected readonly isFiltered = computed(() => this.statusFilter() !== 'ALL');

  ngOnInit(): void {
    this.load();
  }

  /** The internal routing enum, in words. Ops-only — never shown to a client (N4). */
  protected recommendationLabel(rec: PreApplicationRecommendation): string {
    return PRE_RECOMMENDATION_LABELS[rec] ?? rec;
  }

  protected setFilter(value: PreApplicationReviewStatus | 'ALL'): void {
    this.statusFilter.set(value);
    this.pageIndex.set(0);
    this.reload();
  }

  protected toggleOrdering(): void {
    this.ordering.set(this.ordering() === '-submitted_at' ? 'submitted_at' : '-submitted_at');
    this.reload();
  }

  protected reload(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.rows.set([]);
    this.cdr.markForCheck();
    this.load();
  }

  protected onPage(event: PageEvent): void {
    this.pageSize.set(event.pageSize);
    this.pageIndex.set(event.pageIndex);
  }

  private load(): void {
    const status = this.statusFilter();
    this.preApplications
      .opsList({
        ...(status === 'ALL' ? {} : { review_status: status }),
        ordering: this.ordering(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.rows.set(res.pre_applications);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }
}
